import { seedFor } from './clock';
import { prngFromSeed, gauss } from './prng';

// Anchor for long-term drift — fixed so all players share the same baseline math.
const DRIFT_ANCHOR_MS = Date.UTC(2026, 0, 1);

export interface PriceConfig {
  ticker: string;
  baseline: number;     // base price at the drift anchor
  drift: number;        // per-day log drift (positive = trending up)
  volatility: number;   // amplitude multiplier for noise+oscillation
  category: 'company' | 'crypto' | 'mood' | 'prediction' | 'geo';
}

/**
 * Active price impulses — temporary log-space deltas added to a ticker's
 * deterministic price during a window. Used by event triggers (storyline
 * drips, scheduled events) to move prices visibly without breaking the
 * deterministic price model for unrelated instruments.
 */
interface PriceImpulse {
  ticker: string;
  startMs: number;
  durationMs: number;
  logDelta: number;     // peak log delta (e.g., 0.03 ≈ +3%)
}

const activeImpulses: PriceImpulse[] = [];

export function addImpulse(impulse: PriceImpulse): void {
  activeImpulses.push(impulse);
  // GC ONLY very-old impulses (>7 days). Keeping recent impulses is REQUIRED for
  // chart-history accuracy: removing an impulse retroactively makes past prices
  // appear to "rewrite" themselves on the chart, which is a confusing bug.
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  for (let i = activeImpulses.length - 1; i >= 0; i--) {
    const imp = activeImpulses[i]!;
    if (imp.startMs + imp.durationMs < cutoff) {
      activeImpulses.splice(i, 1);
    }
  }
}

/** Returns whether the given ticker currently has any active impulse (within its window). */
export function tickerHasActiveImpulse(ticker: string): boolean {
  const now = Date.now();
  for (const imp of activeImpulses) {
    if (imp.ticker !== ticker) continue;
    if (now < imp.startMs) continue;
    if (now > imp.startMs + imp.durationMs) continue;
    return true;
  }
  return false;
}

/** Returns all impulses for a ticker that started within [fromMs, toMs]. */
export function getImpulsesForTicker(ticker: string, fromMs: number, toMs: number): { startMs: number; logDelta: number }[] {
  return activeImpulses
    .filter((i) => i.ticker === ticker && i.startMs >= fromMs && i.startMs <= toMs)
    .map((i) => ({ startMs: i.startMs, logDelta: i.logDelta }));
}

function impulseLogDelta(ticker: string, t: number): number {
  let delta = 0;
  for (const imp of activeImpulses) {
    if (imp.ticker !== ticker) continue;
    const elapsed = t - imp.startMs;
    if (elapsed < 0 || elapsed > imp.durationMs) continue;
    const phase = elapsed / imp.durationMs;
    // ramp up over first 5%, hold, decay over last 30%
    let strength: number;
    if (phase < 0.05) strength = phase / 0.05;
    else if (phase > 0.7) strength = (1 - phase) / 0.3;
    else strength = 1;
    delta += imp.logDelta * strength;
  }
  // Tanh-cap so multiple S-tier events stacking can't compound into +7000%.
  // Asymmetric: max ~ +400% (log 1.6) up, -80% (log -1.6) down.
  // Big presidential events still feel huge but the tape doesn't go to the moon.
  return Math.tanh(delta / 1.6) * 1.6;
}

/**
 * Multi-scale fractal noise. Each scale (1min, 5min, 30min, 4h, 24h) has its
 * own deterministic gauss-per-bucket. Linear-interpolated between adjacent
 * buckets so the value moves continuously. Mean-reverting at long scales (no
 * runaway), but trending at short scales (no perfect support/resistance).
 * Same wall-clock t → same value for every client.
 */
const SCALE_BUCKETS_MS = [60_000, 5 * 60_000, 30 * 60_000, 4 * 60 * 60_000, 24 * 60 * 60_000] as const;
const SCALE_AMPS = [1.0, 1.4, 1.8, 1.6, 1.2] as const; // longer scales weighted slightly higher → real trends
const FRACTAL_ANCHOR_MS = Date.UTC(2026, 0, 1);

function scaleNoise(ticker: string, t: number, scaleIdx: number): number {
  const bucketMs = SCALE_BUCKETS_MS[scaleIdx]!;
  const offset = t - FRACTAL_ANCHOR_MS;
  const bucket = Math.floor(offset / bucketMs);
  const frac = (offset % bucketMs) / bucketMs;
  const a = scaleGaussAt(ticker, scaleIdx, bucket);
  const b = scaleGaussAt(ticker, scaleIdx, bucket + 1);
  // Smoothstep interp so the chart isn't a sharp triangle wave at scale boundaries
  const smooth = frac * frac * (3 - 2 * frac);
  return a + (b - a) * smooth;
}

function scaleGaussAt(ticker: string, scaleIdx: number, bucket: number): number {
  const key = `noise:${ticker}:${scaleIdx}`;
  const seed = seedFor(key, bucket, 1);
  const rng = prngFromSeed(seed);
  return gauss(rng);
}

/**
 * Returns a deterministic price for `cfg` at wall-clock time `t` (ms).
 * Same `(t, cfg)` always yields the same price across all clients.
 *
 * Mixes:
 *   - long-term drift (per-day log-drift from a fixed anchor)
 *   - multi-scale fractal noise (1m / 5m / 30m / 4h / 24h gauss buckets, smoothstep-interpolated)
 *     — gives prices wandering at every visible time scale, no perfect support/resistance
 *     — bounded by definition (each scale has finite std), so prices don't run away to infinity
 *   - intra-second oscillation (smooth sin sums for "breathing" charts)
 *   - impulses (event-triggered short-window deltas, tanh-capped to ±400%)
 */
export function priceAt(cfg: PriceConfig, t: number): number {
  const days = (t - DRIFT_ANCHOR_MS) / 86_400_000;
  const driftMult = Math.exp(cfg.drift * days);

  // Multi-scale fractal noise — sum across 5 time scales for a self-similar wandering chart.
  // Each scale contributes ~vol × scaleAmp magnitude in log space.
  let fractal = 0;
  for (let s = 0; s < SCALE_BUCKETS_MS.length; s++) {
    fractal += scaleNoise(cfg.ticker, t, s) * SCALE_AMPS[s]!;
  }

  // Per-minute high-frequency noise (existing — adds tape jitter within a minute)
  const minuteSeed = seedFor(`price:${cfg.ticker}`, t, 60_000);
  const minuteRng = prngFromSeed(minuteSeed);
  let noiseSum = 0;
  for (let i = 0; i < 4; i++) noiseSum += gauss(minuteRng);
  const minuteNoise = noiseSum / 2;

  // Intra-second oscillation (existing — gives "breathing" feel)
  const oscillation =
    Math.sin(t / 7000) * 0.4 +
    Math.sin(t / 1700) * 0.2 +
    Math.sin(t / 530) * 0.1;

  // Total log change: fractal noise dominates the chart shape (real wandering,
  // multi-scale trends), per-minute noise + oscillation add tape jitter,
  // impulses fire on news. fractal × volatility × 0.6 keeps the magnitude
  // realistic (each scale unit ≈ 1.5%-3% range for typical vol).
  const totalLogChange =
    cfg.volatility * 0.6 * fractal
    + cfg.volatility * (minuteNoise + oscillation)
    + impulseLogDelta(cfg.ticker, t);

  return Math.max(0.01, cfg.baseline * driftMult * Math.exp(totalLogChange));
}

/** Convenience: emit a series of (time, price) pairs for charting. */
export function priceSeries(cfg: PriceConfig, fromMs: number, toMs: number, stepMs: number): { time: number; price: number }[] {
  const out: { time: number; price: number }[] = [];
  for (let t = fromMs; t <= toMs; t += stepMs) {
    out.push({ time: t, price: priceAt(cfg, t) });
  }
  return out;
}
